import { useState, useEffect } from 'react';
import { X, MessageCircle, Package, Clock, ShoppingCart, MapPin, Ticket, Wallet, Truck, ShieldCheck, ExternalLink, ChevronDown, ChevronUp, TriangleAlert as AlertTriangle, FileDown, Tag, Printer } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import OrderStatusBadge from './OrderStatusBadge';
import PaymentStatusBadge from './PaymentStatusBadge';
import InventoryDeductionDialog from './InventoryDeductionDialog';
import type { InventoryItemInfo } from './InventoryDeductionDialog';
import { updateOrderStatus, updateOrderTracking, fetchOrderInventoryInfo } from '@/lib/orderService';
import { OrderShippingAddress } from '@/components/buyer/OrderShippingAddress';
import { OrderPickupInfo } from '@/components/buyer/OrderPickupInfo';
import { deductStockForOrder, restoreStockForOrder } from '@/lib/stockUtils';
import { refundOrderPayment } from '@/lib/merchantPayments';
import { getShippingCredentialsConfig } from '@/lib/merchantShipping';
import {
  getOrderShippingLabel,
  purchaseOrderShippingLabel,
  cancelOrderShippingLabel,
  type OrderShippingLabel,
} from '@/lib/merchantShippingLabel';
import { useInventoryEnabled } from '@/hooks/useInventoryEnabled';
import { generateWhatsAppUrl } from '@/lib/utils';
import { formatCpfCnpj } from '@/lib/document';
import { toast } from 'sonner';
import { logActivity } from '@/lib/activityLogger';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import type { Order, OrderStatus, OrderPaymentStatus } from '@/types';
import { useAuth } from '@/contexts/AuthContext';
import { formatOrderMoney } from '@/lib/orderPrint';

interface OrderPaymentRow {
  id: string;
  payment_method: string;
  status: string;
  status_detail: string;
  amount_cents: number;
  card_last4: string;
  created_at: string;
}

interface OrderDetailsPanelProps {
  order: Order | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onStatusUpdate: (orderId: string, newStatus: OrderStatus) => void;
  onTrackingUpdate?: (orderId: string, tracking: { carrier: string | null; tracking_code: string | null }) => void;
  onPaymentStatusUpdate?: (orderId: string, paymentStatus: OrderPaymentStatus) => void;
}

const STATUS_TRANSITIONS: Record<string, { label: string; next: OrderStatus }[]> = {
  pending: [
    { label: 'Confirmar Pedido', next: 'confirmed' },
    { label: 'Cancelar', next: 'cancelled' },
  ],
  confirmed: [
    { label: 'Marcar como Preparando', next: 'preparing' },
    { label: 'Cancelar', next: 'cancelled' },
  ],
  preparing: [
    { label: 'Marcar como Enviado', next: 'shipped' },
  ],
  shipped: [
    { label: 'Marcar como Entregue', next: 'delivered' },
  ],
  delivered: [],
  cancelled: [],
};

// Mirrors SERVICE_CARRIER_NAMES in supabase/functions/merchant-shipping-label
// — used here only to show a friendly name while a purchase is in flight,
// before the order's own `carrier` column (written by that edge function)
// comes back through onTrackingUpdate.
const SERVICE_CARRIER_NAMES: Record<string, string> = {
  '1': 'Correios (PAC)',
  '2': 'Correios (SEDEX)',
  '17': 'Correios (Mini Envios)',
  '3': 'Jadlog',
  '33': 'J&T',
  '31': 'Loggi',
};

const LABEL_STATUS_LABELS: Record<OrderShippingLabel['status'], string> = {
  pending: 'Aguardando pagamento',
  released: 'Etiqueta comprada',
  posted: 'Postada',
  delivered: 'Entregue',
  cancelled: 'Cancelada',
  error: 'Falha na compra',
};

export default function OrderDetailsPanel({
  order,
  open,
  onOpenChange,
  onStatusUpdate,
  onTrackingUpdate,
  onPaymentStatusUpdate,
}: OrderDetailsPanelProps) {
  const [updating, setUpdating] = useState(false);
  const { user } = useAuth();
  const formatCurrency = (value: number) => formatOrderMoney(value, user?.currency);
  const { inventoryEnabled, autoDeductStock } = useInventoryEnabled();
  const [inventoryDialogOpen, setInventoryDialogOpen] = useState(false);
  const [inventoryDialogMode, setInventoryDialogMode] = useState<'deduct' | 'restore'>('deduct');
  const [inventoryItems, setInventoryItems] = useState<InventoryItemInfo[]>([]);
  const [pendingStatus, setPendingStatus] = useState<OrderStatus | null>(null);
  const [colorImageMap, setColorImageMap] = useState<Record<string, string>>({});
  const [paymentHistoryOpen, setPaymentHistoryOpen] = useState(false);
  const [paymentHistory, setPaymentHistory] = useState<OrderPaymentRow[] | null>(null);
  const [paymentHistoryLoading, setPaymentHistoryLoading] = useState(false);
  const [carrier, setCarrier] = useState('');
  const [trackingCode, setTrackingCode] = useState('');
  const [savingTracking, setSavingTracking] = useState(false);
  const [refundDialogOpen, setRefundDialogOpen] = useState(false);
  const [refunding, setRefunding] = useState(false);
  const [labelPurchaseEnabled, setLabelPurchaseEnabled] = useState(false);
  const [shippingLabel, setShippingLabel] = useState<OrderShippingLabel | null>(null);
  const [labelLoading, setLabelLoading] = useState(false);
  const [purchasingLabel, setPurchasingLabel] = useState(false);
  const [cancellingLabel, setCancellingLabel] = useState(false);
  const [purchaseLabelDialogOpen, setPurchaseLabelDialogOpen] = useState(false);
  const [cancelLabelDialogOpen, setCancelLabelDialogOpen] = useState(false);

  useEffect(() => {
    setPaymentHistoryOpen(false);
    setPaymentHistory(null);
    setCarrier(order?.carrier || '');
    setTrackingCode(order?.tracking_code || '');
    setRefundDialogOpen(false);
    setPurchaseLabelDialogOpen(false);
    setCancelLabelDialogOpen(false);
  }, [order?.id]);

  useEffect(() => {
    getShippingCredentialsConfig()
      .then(({ config }) => setLabelPurchaseEnabled(!!config?.label_purchase_enabled))
      .catch(() => setLabelPurchaseEnabled(false));
  }, []);

  const isSuperFreteOrder = order?.order_type === 'ecommerce' && !!order?.delivery_option?.startsWith('superfrete:');

  useEffect(() => {
    if (!order || !isSuperFreteOrder || !labelPurchaseEnabled) {
      setShippingLabel(null);
      return;
    }
    let cancelled = false;
    setLabelLoading(true);
    getOrderShippingLabel(order.id)
      .then((label) => { if (!cancelled) setShippingLabel(label); })
      .catch(() => { if (!cancelled) setShippingLabel(null); })
      .finally(() => { if (!cancelled) setLabelLoading(false); });
    return () => { cancelled = true; };
  }, [order?.id, isSuperFreteOrder, labelPurchaseEnabled]);

  const handlePurchaseLabel = async () => {
    if (!order) return;
    setPurchasingLabel(true);
    try {
      const label = await purchaseOrderShippingLabel(order.id);
      setShippingLabel(label);
      if (label.status === 'released') {
        toast.success('Etiqueta comprada com sucesso');
        const carrierName = label.service_id ? SERVICE_CARRIER_NAMES[label.service_id] || 'SuperFrete' : 'SuperFrete';
        const tracking = { carrier: carrierName, tracking_code: label.tracking_code };
        setCarrier(carrierName);
        setTrackingCode(label.tracking_code || '');
        onTrackingUpdate?.(order.id, tracking);
      } else {
        toast.error(label.error_message || 'Não foi possível comprar a etiqueta');
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Erro ao comprar etiqueta');
    } finally {
      setPurchasingLabel(false);
      setPurchaseLabelDialogOpen(false);
    }
  };

  const handleCancelLabel = async () => {
    if (!order) return;
    setCancellingLabel(true);
    try {
      await cancelOrderShippingLabel(order.id);
      setShippingLabel((prev) => (prev ? { ...prev, status: 'cancelled' } : prev));
      setCarrier('');
      setTrackingCode('');
      onTrackingUpdate?.(order.id, { carrier: null, tracking_code: null });
      toast.success('Etiqueta cancelada');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Erro ao cancelar etiqueta');
    } finally {
      setCancellingLabel(false);
      setCancelLabelDialogOpen(false);
    }
  };

  const handleRefund = async () => {
    if (!order) return;
    setRefunding(true);
    try {
      const { data: payment, error: paymentError } = await supabase
        .from('order_payments')
        .select('id')
        .eq('order_id', order.id)
        .eq('status', 'approved')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (paymentError || !payment) {
        toast.error('Pagamento aprovado não encontrado para este pedido');
        return;
      }

      await refundOrderPayment(payment.id);
      toast.success('Pagamento reembolsado com sucesso');
      onPaymentStatusUpdate?.(order.id, 'refunded');
      if (paymentHistoryOpen) {
        const { data } = await supabase
          .from('order_payments')
          .select('id, payment_method, status, status_detail, amount_cents, card_last4, created_at')
          .eq('order_id', order.id)
          .order('created_at', { ascending: false });
        setPaymentHistory(data || []);
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Erro ao reembolsar pagamento');
    } finally {
      setRefunding(false);
      setRefundDialogOpen(false);
    }
  };

  const handleSaveTracking = async () => {
    if (!order) return;
    setSavingTracking(true);
    const tracking = { carrier: carrier.trim() || null, tracking_code: trackingCode.trim() || null };
    const success = await updateOrderTracking(order.id, tracking);
    if (success) {
      toast.success('Rastreio salvo');
      onTrackingUpdate?.(order.id, tracking);
    } else {
      toast.error('Erro ao salvar rastreio');
    }
    setSavingTracking(false);
  };

  const togglePaymentHistory = async () => {
    if (!order) return;
    if (paymentHistoryOpen) {
      setPaymentHistoryOpen(false);
      return;
    }
    setPaymentHistoryOpen(true);
    if (paymentHistory === null) {
      setPaymentHistoryLoading(true);
      const { data } = await supabase
        .from('order_payments')
        .select('id, payment_method, status, status_detail, amount_cents, card_last4, created_at')
        .eq('order_id', order.id)
        .order('created_at', { ascending: false });
      setPaymentHistory(data || []);
      setPaymentHistoryLoading(false);
    }
  };

  useEffect(() => {
    if (!order) return;
    const itemsWithColor = (order.order_items || []).filter(
      (item) => item.product_id && item.selected_color
    );
    if (itemsWithColor.length === 0) {
      setColorImageMap({});
      return;
    }

    const fetchColorImages = async () => {
      const productIds = [...new Set(itemsWithColor.map((i) => i.product_id))];
      const { data } = await supabase
        .from('product_images')
        .select('product_id, url, associated_color')
        .in('product_id', productIds)
        .not('associated_color', 'is', null);

      if (!data) return;

      const map: Record<string, string> = {};
      for (const item of itemsWithColor) {
        const key = `${item.id}`;
        const match = data.find(
          (img) =>
            img.product_id === item.product_id &&
            img.associated_color === item.selected_color
        );
        if (match) map[key] = match.url;
      }
      setColorImageMap(map);
    };

    fetchColorImages();
  }, [order?.id]);

  if (!order) return null;

  const transitions = STATUS_TRANSITIONS[order.status] || [];
  const items = order.order_items || [];

  const executeStatusChange = async (newStatus: OrderStatus) => {
    setUpdating(true);
    const success = await updateOrderStatus(order.id, newStatus);
    if (success) {
      const statusLabels: Record<string, string> = {
        pending: 'pendente', confirmed: 'confirmado', preparing: 'em preparo',
        shipped: 'enviado', delivered: 'entregue', cancelled: 'cancelado',
      };
      logActivity('order.status_change', `Atualizou pedido de ${order.customer_name} para "${statusLabels[newStatus] || newStatus}"`, 'order', order.id);
      onStatusUpdate(order.id, newStatus);
      toast.success(
        newStatus === 'cancelled'
          ? 'Pedido cancelado'
          : 'Status atualizado'
      );
    } else {
      toast.error('Erro ao atualizar status');
    }
    setUpdating(false);
  };

  const handleStatusChange = async (newStatus: OrderStatus) => {
    if (!inventoryEnabled) {
      await executeStatusChange(newStatus);
      return;
    }

    if (newStatus === 'confirmed' && !autoDeductStock) {
      setUpdating(true);
      const invItems = await fetchOrderInventoryInfo(order.id);
      setUpdating(false);

      const trackedItems = invItems.filter((i) => i.track_inventory);
      if (trackedItems.length > 0) {
        setInventoryItems(invItems);
        setInventoryDialogMode('deduct');
        setPendingStatus(newStatus);
        setInventoryDialogOpen(true);
        return;
      }
    }

    if (newStatus === 'cancelled' && order.inventory_deducted) {
      setUpdating(true);
      const invItems = await fetchOrderInventoryInfo(order.id);
      setUpdating(false);

      const trackedItems = invItems.filter((i) => i.track_inventory);
      if (trackedItems.length > 0) {
        setInventoryItems(invItems);
        setInventoryDialogMode('restore');
        setPendingStatus(newStatus);
        setInventoryDialogOpen(true);
        return;
      }
    }

    await executeStatusChange(newStatus);
  };

  const handleInventoryConfirm = async () => {
    if (!pendingStatus || !user?.id) return;

    if (inventoryDialogMode === 'deduct') {
      const deductionItems = inventoryItems
        .filter((i) => i.track_inventory)
        .map((i) => ({
          product_id: i.product_id,
          quantity: i.quantity,
          selected_color: i.selected_color,
          selected_size: i.selected_size,
          selected_flavor: i.selected_flavor,
          selected_variant_label: i.selected_variant_label,
        }));

      const result = await deductStockForOrder(order.id, user.id, deductionItems);

      const pendencias = result.insufficientItems.length + result.unmatchedItems.length;
      if (!result.success) {
        toast.error('Nao foi possivel baixar o estoque deste pedido.');
      } else if (pendencias > 0) {
        toast.warning(
          `Baixa parcial: ${pendencias} ${pendencias === 1 ? 'item ficou' : 'itens ficaram'} pendente(s). Confira o aviso no pedido.`
        );
      }
    } else {
      // A quantidade a estornar vem dos movimentos de saida do proprio pedido
      // (dentro da RPC), nao dos itens exibidos aqui — baixa parcial estorna
      // parcial.
      const restored = await restoreStockForOrder(order.id);
      if (!restored) {
        toast.error('Nao foi possivel estornar o estoque deste pedido.');
      }
    }

    await executeStatusChange(pendingStatus);
    setPendingStatus(null);
  };

  const handleInventorySkip = async () => {
    if (pendingStatus) {
      await executeStatusChange(pendingStatus);
      setPendingStatus(null);
    }
  };

  const whatsappUrl = order.customer_whatsapp
    ? generateWhatsAppUrl(
        order.customer_whatsapp,
        `Ola ${order.customer_name}, sobre o seu pedido...`,
        order.customer_country_code || '55'
      )
    : '';

  // Falta de saldo e combinacao inexistente na grade sao a mesma coisa para o
  // lojista: o pedido entrou mas o estoque nao acompanhou. `available: null`
  // distingue os dois casos na hora de escrever a linha.
  const shortfallItems = [
    ...(order.stock_shortfall?.insufficient_items ?? []),
    ...(order.stock_shortfall?.unmatched_items ?? []).map((i) => ({ ...i, available: null })),
  ];

  const isEcommerce = order.order_type === 'ecommerce';
  const isPickupOrder = order.delivery_scope === 'pickup';
  const hasShippingAddress = !isPickupOrder && !!(order.shipping_street || order.shipping_city);
  const paymentNeedsAttention = isEcommerce && (order.payment_status === 'pending' || order.payment_status === 'rejected');

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className="sm:max-w-lg overflow-y-auto">
          <SheetHeader className="pb-4">
            <div className="flex items-center justify-between gap-2 pr-8">
              <SheetTitle className="text-lg">Detalhes do Pedido</SheetTitle>
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5 shrink-0"
                onClick={() => window.open(`/dashboard/orders/${order.id}/imprimir?print=1`, '_blank')}
              >
                <Printer className="h-3.5 w-3.5" />
                Imprimir / PDF
              </Button>
            </div>
            <div className="flex items-center gap-2 mt-1">
              <OrderStatusBadge status={order.status} />
              {isEcommerce && <PaymentStatusBadge status={order.payment_status} />}
              <Badge variant="outline" className="text-xs">
                {order.source === 'cart' ? 'Carrinho' : 'Pagina do Produto'}
              </Badge>
              {order.inventory_deducted && (
                <Badge variant="outline" className="text-xs text-emerald-600 border-emerald-300">
                  Estoque reduzido
                </Badge>
              )}
              {shortfallItems.length > 0 && (
                <Badge variant="outline" className="text-xs text-amber-600 border-amber-300">
                  Estoque pendente
                </Badge>
              )}
            </div>
          </SheetHeader>

          <div className="space-y-6">
            {shortfallItems.length > 0 && (
              <div className="rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950/30 p-4 space-y-2">
                <div className="flex items-center gap-2 text-amber-700 dark:text-amber-400">
                  <AlertTriangle className="h-4 w-4 shrink-0" />
                  <p className="text-sm font-semibold">Baixa de estoque incompleta</p>
                </div>
                <p className="text-xs text-amber-700/90 dark:text-amber-400/90">
                  Este pedido foi registrado, mas nem tudo pôde ser retirado do
                  estoque. Confira antes de separar:
                </p>
                <ul className="space-y-1 text-xs text-amber-800 dark:text-amber-300">
                  {shortfallItems.map((item, idx) => {
                    const variante = [item.selected_color, item.selected_size, item.selected_flavor]
                      .filter(Boolean)
                      .join(' / ');
                    return (
                      <li key={`${item.product_id}-${idx}`} className="flex gap-1.5">
                        <span aria-hidden>•</span>
                        <span>
                          <strong>{item.product_title}</strong>
                          {variante && ` (${variante})`}:{' '}
                          {item.available == null
                            ? `combinação não encontrada na grade de variantes — pedido de ${item.requested}`
                            : `pedido ${item.requested}, baixado ${item.deducted ?? 0} (havia ${item.available})`}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
            {/* Customer Info */}
            <div className="space-y-3">
              <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Cliente</h4>
              <div className="bg-muted/30 rounded-lg p-4 space-y-2">
                <p className="font-medium">{order.customer_name}</p>
                <p className="text-sm text-muted-foreground">
                  +{order.customer_country_code} {order.customer_whatsapp}
                </p>
                {order.customer_cpf && (
                  <p className="text-sm text-muted-foreground">CPF: {formatCpfCnpj(order.customer_cpf)}</p>
                )}
                {whatsappUrl && (
                  <Button variant="outline" size="sm" className="mt-2 gap-1.5" asChild>
                    <a href={whatsappUrl} target="_blank" rel="noopener noreferrer">
                      <MessageCircle className="h-3.5 w-3.5" />
                      Falar no WhatsApp
                    </a>
                  </Button>
                )}
              </div>
            </div>

            <Separator />

            {/* Order Items */}
            <div className="space-y-3">
              <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
                Itens ({items.length})
              </h4>
              <div className="space-y-3">
                {items.map((item) => {
                  const productUrl = user?.slug
                    ? `/${user.slug}/produtos/${item.product_id}${item.selected_color ? `?cor=${encodeURIComponent(item.selected_color)}` : ''}`
                    : null;

                  return (
                  <div key={item.id} className="flex gap-3 p-3 border rounded-lg">
                    <a
                      href={productUrl || '#'}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`w-14 h-14 rounded-md overflow-hidden bg-muted flex-shrink-0 ${productUrl ? 'cursor-pointer ring-offset-2 hover:ring-2 hover:ring-primary/50 transition-all' : ''}`}
                      onClick={(e) => { if (!productUrl) e.preventDefault(); }}
                    >
                      {(colorImageMap[item.id] || item.product_image_url) ? (
                        <img
                          src={colorImageMap[item.id] || item.product_image_url}
                          alt={item.product_title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Package className="h-5 w-5 text-muted-foreground" />
                        </div>
                      )}
                    </a>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1">
                        {productUrl ? (
                          <a
                            href={productUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm font-medium line-clamp-1 hover:text-primary hover:underline transition-colors"
                          >
                            {item.product_title}
                          </a>
                        ) : (
                          <p className="text-sm font-medium line-clamp-1">{item.product_title}</p>
                        )}
                        {productUrl && item.selected_color && (
                          <ExternalLink className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                        )}
                      </div>
                      <div className="flex flex-wrap gap-1.5 mt-1">
                        {item.selected_color && (
                          <span className="text-xs text-muted-foreground">Cor: {item.selected_color}</span>
                        )}
                        {item.selected_size && (
                          <span className="text-xs text-muted-foreground">Tam: {item.selected_size}</span>
                        )}
                        {item.selected_flavor && (
                          <span className="text-xs text-muted-foreground">Sabor: {item.selected_flavor}</span>
                        )}
                        {item.selected_variant_label && (
                          <span className="text-xs text-muted-foreground">{item.selected_variant_label}</span>
                        )}
                      </div>
                      {item.item_notes && (
                        <p className="text-xs text-muted-foreground/70 mt-1 italic">{item.item_notes}</p>
                      )}
                      <div className="flex items-center justify-between mt-1.5">
                        <span className="text-xs text-muted-foreground">
                          {item.quantity}x {formatCurrency(item.unit_price)}
                        </span>
                        <span className="text-sm font-semibold">
                          {formatCurrency(item.subtotal)}
                        </span>
                      </div>
                    </div>
                  </div>
                  );
                })}
              </div>
            </div>

            <Separator />

            {/* Price Breakdown */}
            {(order.coupon_code || order.payment_method || order.delivery_option || (order.insurance_fee && order.insurance_fee > 0)) && (
              <>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Subtotal</span>
                    <span className="text-sm">{formatCurrency(order.subtotal)}</span>
                  </div>
                  {order.coupon_code && (
                    <div className="flex items-center justify-between text-green-600 dark:text-green-400">
                      <span className="text-sm flex items-center gap-1.5">
                        <Ticket className="h-3.5 w-3.5" />
                        Cupom {order.coupon_code}
                      </span>
                      <span className="text-sm font-medium">-{formatCurrency(order.discount_amount || 0)}</span>
                    </div>
                  )}
                  {order.payment_method && order.payment_method_discount && order.payment_method_discount > 0 && (
                    <div className="flex items-center justify-between text-green-600 dark:text-green-400">
                      <span className="text-sm flex items-center gap-1.5">
                        <Wallet className="h-3.5 w-3.5" />
                        Desc. {order.payment_method}
                      </span>
                      <span className="text-sm font-medium">-{formatCurrency(order.payment_method_discount)}</span>
                    </div>
                  )}
                  {order.delivery_option && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground flex items-center gap-1.5">
                        <Truck className="h-3.5 w-3.5" />
                        Entrega: {order.delivery_option}
                      </span>
                      <span className={`text-sm font-medium ${order.delivery_is_quote ? 'text-amber-600 dark:text-amber-400' : ''}`}>
                        {order.delivery_is_quote
                          ? 'A combinar'
                          : order.delivery_fee && order.delivery_fee > 0
                            ? `+${formatCurrency(order.delivery_fee)}`
                            : 'Gratis'
                        }
                      </span>
                    </div>
                  )}
                  {!!order.insurance_fee && order.insurance_fee > 0 && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground flex items-center gap-1.5">
                        <ShieldCheck className="h-3.5 w-3.5" />
                        Seguro de frete
                      </span>
                      <span className="text-sm font-medium">
                        +{formatCurrency(order.insurance_fee)}
                      </span>
                    </div>
                  )}
                </div>
                <Separator />
              </>
            )}

            {/* Payment Method (when no price breakdown section) */}
            {order.payment_method && !order.coupon_code && !order.delivery_option && !(order.insurance_fee && order.insurance_fee > 0) && !(order.payment_method_discount && order.payment_method_discount > 0) && (
              <>
                <div className="flex items-center gap-1.5 text-sm">
                  <Wallet className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="text-muted-foreground">Pagamento:</span>
                  <span className="font-medium">{order.payment_method}</span>
                </div>
                <Separator />
              </>
            )}

            {/* Order Total */}
            <div className="flex justify-between items-center">
              <span className="text-base font-semibold">Total</span>
              <span className="text-xl font-bold text-primary">
                {formatCurrency(order.total)}
              </span>
            </div>

            {/* Payment Method Label (shown under total when breakdown section already present) */}
            {order.payment_method && (order.coupon_code || order.delivery_option || (order.insurance_fee && order.insurance_fee > 0) || (order.payment_method_discount && order.payment_method_discount > 0)) && (
              <div className="flex items-center gap-1.5 text-sm">
                <Wallet className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="text-muted-foreground">Pagamento:</span>
                <span className="font-medium">{order.payment_method}</span>
              </div>
            )}

            {/* Order Info */}
            <div className="text-xs text-muted-foreground space-y-1">
              <div className="flex items-center gap-1.5">
                <Clock className="h-3 w-3" />
                {format(new Date(order.created_at), "dd 'de' MMMM 'de' yyyy 'as' HH:mm", { locale: ptBR })}
              </div>
            </div>

            {isPickupOrder && (
              <>
                <Separator />
                <div className="space-y-2">
                  <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
                    <MapPin className="h-3.5 w-3.5" />
                    Retirada na loja
                  </h4>
                  <div className="bg-muted/30 rounded-lg p-4">
                    <OrderPickupInfo order={order} store={{ city: user?.city, state: user?.state }} />
                  </div>
                </div>
              </>
            )}

            {hasShippingAddress && (
              <>
                <Separator />
                <div className="space-y-2">
                  <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
                    <MapPin className="h-3.5 w-3.5" />
                    Endereço de entrega
                  </h4>
                  <div className="bg-muted/30 rounded-lg p-4">
                    <OrderShippingAddress address={order} />
                  </div>
                </div>
              </>
            )}

            {isSuperFreteOrder && labelPurchaseEnabled && (
              <>
                <Separator />
                <div className="space-y-2">
                  <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
                    <Tag className="h-3.5 w-3.5" />
                    Etiqueta SuperFrete
                  </h4>
                  <div className="bg-muted/30 rounded-lg p-4 space-y-3">
                    {labelLoading ? (
                      <p className="text-xs text-muted-foreground">Carregando...</p>
                    ) : !shippingLabel || shippingLabel.status === 'cancelled' || shippingLabel.status === 'error' ? (
                      <>
                        {shippingLabel?.status === 'error' && (
                          <p className="text-xs text-red-600 dark:text-red-400">
                            {shippingLabel.error_message || 'Falha ao comprar a etiqueta.'}
                          </p>
                        )}
                        {shippingLabel?.status === 'cancelled' && (
                          <Badge variant="outline" className="text-xs">Etiqueta cancelada</Badge>
                        )}
                        <Button
                          size="sm"
                          className="w-full gap-1.5"
                          onClick={() => setPurchaseLabelDialogOpen(true)}
                          disabled={purchasingLabel}
                        >
                          <Tag className="h-3.5 w-3.5" />
                          {shippingLabel ? 'Tentar novamente' : 'Comprar etiqueta e gerar rastreio'}
                        </Button>
                      </>
                    ) : (
                      <>
                        <div className="flex items-center justify-between">
                          <Badge variant="outline" className="text-xs">
                            {LABEL_STATUS_LABELS[shippingLabel.status]}
                          </Badge>
                          {shippingLabel.price != null && (
                            <span className="text-sm font-medium">{formatOrderMoney(shippingLabel.price, 'BRL')}</span>
                          )}
                        </div>
                        {shippingLabel.tracking_code && (
                          <p className="text-xs text-muted-foreground">
                            Rastreio: <span className="font-medium text-foreground">{shippingLabel.tracking_code}</span>
                          </p>
                        )}
                        <div className="flex gap-2">
                          {shippingLabel.label_pdf_url && (
                            <Button variant="outline" size="sm" className="gap-1.5" asChild>
                              <a href={shippingLabel.label_pdf_url} target="_blank" rel="noopener noreferrer">
                                <FileDown className="h-3.5 w-3.5" />
                                Baixar etiqueta (PDF)
                              </a>
                            </Button>
                          )}
                          {shippingLabel.status === 'released' && (
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={() => setCancelLabelDialogOpen(true)}
                              disabled={cancellingLabel}
                            >
                              Cancelar etiqueta
                            </Button>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </>
            )}

            {(order.status === 'shipped' || order.status === 'delivered') && (
              <>
                <Separator />
                <div className="space-y-2">
                  <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
                    <Truck className="h-3.5 w-3.5" />
                    Rastreio
                  </h4>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1">
                      <Label htmlFor="carrier" className="text-xs text-muted-foreground">Transportadora</Label>
                      <Input
                        id="carrier"
                        placeholder="Ex: Correios"
                        value={carrier}
                        onChange={(e) => setCarrier(e.target.value)}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="trackingCode" className="text-xs text-muted-foreground">Código de rastreio</Label>
                      <Input
                        id="trackingCode"
                        placeholder="Ex: BR123456789BR"
                        value={trackingCode}
                        onChange={(e) => setTrackingCode(e.target.value)}
                      />
                    </div>
                  </div>
                  <Button variant="outline" size="sm" onClick={handleSaveTracking} disabled={savingTracking}>
                    {savingTracking ? 'Salvando...' : 'Salvar rastreio'}
                  </Button>
                </div>
              </>
            )}

            {isEcommerce && (
              <>
                <Separator />
                <div className="space-y-2">
                  <button
                    type="button"
                    onClick={togglePaymentHistory}
                    className="flex items-center justify-between w-full text-left"
                  >
                    <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
                      Histórico de pagamento
                    </h4>
                    {paymentHistoryOpen ? (
                      <ChevronUp className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <ChevronDown className="h-4 w-4 text-muted-foreground" />
                    )}
                  </button>
                  {paymentHistoryOpen && (
                    <div className="space-y-2">
                      {paymentHistoryLoading ? (
                        <p className="text-xs text-muted-foreground">Carregando...</p>
                      ) : (paymentHistory || []).length === 0 ? (
                        <p className="text-xs text-muted-foreground">Nenhuma tentativa de pagamento registrada.</p>
                      ) : (
                        (paymentHistory || []).map((payment) => (
                          <div key={payment.id} className="flex items-center justify-between text-xs bg-muted/30 rounded-lg p-3">
                            <span>
                              {payment.payment_method === 'pix' ? 'Pix' : `Cartão ****${payment.card_last4}`}
                              {' — '}
                              {new Date(payment.created_at).toLocaleString('pt-BR')}
                            </span>
                            <Badge variant="outline" className="text-xs">
                              {payment.status}
                            </Badge>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                  {order.payment_status === 'approved' && (
                    <Button
                      variant="destructive"
                      size="sm"
                      className="w-full"
                      onClick={() => setRefundDialogOpen(true)}
                    >
                      Reembolsar pagamento
                    </Button>
                  )}
                </div>
              </>
            )}

            <Separator />

            {/* Status Actions */}
            {transitions.length > 0 && (
              <div className="space-y-2">
                {paymentNeedsAttention && (
                  <div className="flex items-start gap-1.5 text-xs text-amber-600 bg-amber-500/10 border border-amber-500/20 rounded-lg p-2.5">
                    <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                    <span>Pagamento pendente — confirme o recebimento antes de enviar.</span>
                  </div>
                )}
                {transitions.map((t) => (
                  <Button
                    key={t.next}
                    variant={t.next === 'cancelled' ? 'destructive' : 'default'}
                    className="w-full"
                    disabled={updating}
                    onClick={() => handleStatusChange(t.next)}
                  >
                    {updating ? 'Atualizando...' : t.label}
                  </Button>
                ))}
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>

      <InventoryDeductionDialog
        open={inventoryDialogOpen}
        onOpenChange={setInventoryDialogOpen}
        mode={inventoryDialogMode}
        items={inventoryItems}
        onConfirm={handleInventoryConfirm}
        onSkip={handleInventorySkip}
      />

      <AlertDialog open={refundDialogOpen} onOpenChange={setRefundDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reembolsar este pagamento?</AlertDialogTitle>
            <AlertDialogDescription>
              O valor total será devolvido ao comprador pelo Mercado Pago e o estoque deste pedido será restaurado. Essa ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={refunding}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleRefund();
              }}
              disabled={refunding}
            >
              {refunding ? 'Reembolsando...' : 'Reembolsar'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={purchaseLabelDialogOpen} onOpenChange={setPurchaseLabelDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Comprar etiqueta de envio?</AlertDialogTitle>
            <AlertDialogDescription>
              Isso vai debitar o valor da etiqueta do saldo da sua carteira SuperFrete e gerar o
              código de rastreio automaticamente para este pedido.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={purchasingLabel}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handlePurchaseLabel();
              }}
              disabled={purchasingLabel}
            >
              {purchasingLabel ? 'Comprando...' : 'Comprar etiqueta'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={cancelLabelDialogOpen} onOpenChange={setCancelLabelDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancelar esta etiqueta?</AlertDialogTitle>
            <AlertDialogDescription>
              O valor pago volta para o saldo da sua carteira SuperFrete, desde que a etiqueta
              ainda não tenha sido postada. O rastreio deste pedido será removido.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={cancellingLabel}>Voltar</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleCancelLabel();
              }}
              disabled={cancellingLabel}
            >
              {cancellingLabel ? 'Cancelando...' : 'Cancelar etiqueta'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
